<template>
  <main class="table">
    <!-- Async room: who the game is waiting on (src/store/asyncPlay.js). -->
    <p v-if="waitingBanner" class="waiting-banner" :class="{ 'is-mine': waitingOnMe }">{{ waitingBanner }}</p>
    <section class="status">
      <span class="status__round">Expedition {{ game.meta.round }} of {{ rounds }}</span>
      <span class="status__hazards">
        <!-- The hazards already showing, which is the number that decides
             everything. It's on the path in plain sight anyway; putting the
             tally here just saves squinting at five cards. -->
        <span
          v-for="hazard in hazards"
          :key="hazard.name"
          class="hazard-pip"
          :class="{ 'hazard-pip--armed': hazard.seen }"
          :title="hazard.seen ? `A ${hazard.label} is showing - another ends the expedition` : `No ${hazard.label} yet`"
        >
          <img :src="`${base}card-art/${hazard.name}0.jpg`" :alt="hazard.label">
        </span>
      </span>
      <span class="status__deck" :title="`${cardsLeft} cards still face down`">{{ cardsLeft }} left</span>
    </section>

    <section class="path-region">
      <TemplePath />
      <p class="path-region__pot">
        <template v-if="pathGems">
          <i class="gem" aria-hidden="true" />{{ pathGems }} left on the path
        </template>
        <template v-else>nothing left on the path</template>
        <span v-if="game.artifactsOnPath.length" class="path-region__artifact">
          · {{ game.artifactsOnPath.length }} artifact<template v-if="game.artifactsOnPath.length > 1">s</template>,
          for whoever leaves alone
        </span>
      </p>
    </section>

    <section class="seats-region">
      <SeatList />
    </section>

    <section class="actions">
      <!-- Between expeditions: what happened, and the way on. -->
      <template v-if="game.meta.roundOver">
        <p class="verdict" :class="`verdict--${game.meta.roundOver.reason}`">{{ verdict }}</p>
        <button class="primary" @click="$store.dispatch('nextRound')">
          {{ nextLabel }}
        </button>
      </template>

      <!-- Your decision. -->
      <template v-else-if="awaitingMyChoice">
        <button class="go" @click="choose('continue')">
          <strong>Keep going</strong>
          <small>deeper into the temple</small>
        </button>
        <button class="leave" @click="choose('leave')">
          <strong>Head back</strong>
          <small>{{ leaveSummary }}</small>
        </button>
      </template>

      <!-- Chosen, or out, or watching. -->
      <p v-else class="waiting">{{ waitingText }}</p>
    </section>
  </main>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { waitingLine, waitingOnMe as isWaitingOnMe } from '@/store/asyncPlay.js';
import TemplePath from '@/components/game/TemplePath.vue';
import { listOf } from '@/engine/actions/turnActions';
import SeatList from '@/components/game/SeatList.vue';
import {
  ROUNDS, HAZARDS, HAZARD_LABELS, CHOICES, ROUND_END_REASONS,
} from '@/engine/constants';

// The table. Four regions - status, path, seats, actions - placed by
// grid-template-areas so the same markup stacks on a phone and splits in two
// on a tablet.
//
// The action bar is the whole interface: two buttons, once per turn. It is
// sized `auto` in the grid so it always gets its height first and can never
// be pushed off the bottom, the trap No Thanks! hit and documented.
export default {
  name: 'GameTable',
  components: { TemplePath, SeatList },
  data() {
    return { rounds: ROUNDS, base: process.env.BASE_URL || '/' };
  },
  computed: {
    waitingOnMe() { return isWaitingOnMe(this.$store.state.roomMeta, this.$store.state.user?.uid); },
    waitingBanner() {
      const meta = this.$store.state.roomMeta;
      if (!meta?.async || !this.game) return '';
      const nameOf = (id) => this.game.players.find((p) => p.id === id)?.name || 'someone';
      return waitingLine(meta, this.$store.state.user?.uid, nameOf);
    },
    ...mapState(['game']),
    ...mapGetters(['me', 'awaitingMyChoice', 'pathGems', 'cardsLeft', 'choicesRevealed', 'isOnline']),

    hazards() {
      return HAZARDS.map((name) => ({
        name,
        label: HAZARD_LABELS[name] || name,
        seen: (this.game.hazardsSeen[name] || 0) > 0,
      }));
    },

    /** What leaving right now would actually be worth, spelled out. */
    leaveSummary() {
      const inTemple = this.game.players.filter((p) => p.inTemple).length;
      const parts = [];
      if (this.me?.pocket) parts.push(`bank ${this.me.pocket}`);
      if (this.pathGems) {
        parts.push(inTemple > 1 ? `share up to ${this.pathGems}` : `take all ${this.pathGems}`);
      }
      if (this.game.artifactsOnPath.length) parts.push('artifacts if you go alone');
      return parts.length ? parts.join(', ') : 'with nothing';
    },

    verdict() {
      const over = this.game.meta.roundOver;
      if (!over) return '';
      if (over.reason === ROUND_END_REASONS.HAZARD) {
        const caught = this.game.players.filter((p) => p.caught);
        const who = caught.length === this.game.players.length
          ? 'Everyone still inside'
          : listOf(caught.map((p) => p.name));
        return `A second ${HAZARD_LABELS[over.hazard] || over.hazard}. ${who} dropped everything and ran.`;
      }
      return 'Everyone made it out. The temple is sealed.';
    },

    nextLabel() {
      return this.game.meta.round >= ROUNDS ? 'Open the tents' : `Expedition ${this.game.meta.round + 1}`;
    },

    waitingText() {
      if (!this.me) return 'Watching.';
      if (!this.me.inTemple) {
        const still = this.game.players.filter((p) => p.inTemple).length;
        if (!still) return 'Everyone is out.';
        return `You're back at camp. ${still} still inside.`;
      }
      if (this.choicesRevealed) return 'Torches out…';
      const waiting = this.game.players.filter((p) => p.inTemple && p.choice === null);
      if (waiting.length === 1 && waiting[0].id === this.me.id) return 'Your move.';
      return `Waiting for ${listOf(waiting.map((p) => p.name))}`;
    },
  },
  methods: {
    choose(which) {
      this.$store.dispatch('choose', which === 'leave' ? CHOICES.LEAVE : CHOICES.CONTINUE);
    },
  },
};
</script>

<style scoped>
.waiting-banner { margin: 0; padding: 0.5rem 0.9rem; border-radius: 0.6rem; background: rgba(0,0,0,0.08); font-size: 0.95rem; }
.waiting-banner.is-mine { background: rgba(0,0,0,0.16); font-weight: 700; }
</style>